import ApplicationController from "./application_controller"
import Logger from '../lib/logger'

/**
 * Autocomplete Controller - Consolidated autocomplete functionality
 * Combines features from both autocomplete_controller.js and search_autocomplete_controller.js
 */
export default class extends ApplicationController {
  static targets = ['input', 'hidden', 'results']
  static values = { 
    url: String,
    minLength: Number,
    debounceMs: Number,
    searchMode: Boolean,
    commandKEnabled: Boolean,
    idElement: String,
    updateElements: Object
  }

  connect() {
    this.minLengthValue = this.minLengthValue || 2
    this.debounceMsValue = this.debounceMsValue || 300
    this.searchModeValue = this.searchModeValue || false
    this.commandKEnabledValue = this.commandKEnabledValue || false

    this.setupAutocomplete()
    this.initCommandK()

    this.committedLabel = this.inputElement ? this.inputElement.value.trim() : ''
  }

  disconnect() {
    this.removeEventListeners()
    if (this.onCommandK) {
      document.removeEventListener('keydown', this.onCommandK)
    }
  }

  setupAutocomplete() {
    // If targets are missing, try to find them via data attributes or create them
    if (!this.hasInputTarget) {
      this.inputElement = this.element.tagName === 'INPUT' ? this.element : this.element.querySelector('input')
    } else {
      this.inputElement = this.inputTarget
    }

    if (!this.inputElement) {
      Logger.error('Autocomplete controller: no input element found', { element: this.element })
      return
    }

    if (!this.hasResultsTarget) {
      if (this.element.tagName === 'INPUT') {
        this.resultsElement = this.element.parentNode.querySelector('.dropdown-menu') || 
                              this.element.parentNode.querySelector('.autocomplete-results')
      } else {
        this.resultsElement = this.element.querySelector('.dropdown-menu') || 
                              this.element.querySelector('.autocomplete-results')
      }
      
      if (!this.resultsElement) {
        this.resultsElement = document.createElement('ul')
        this.resultsElement.className = 'dropdown-menu autocomplete-results w-100 mt-1'
        this.resultsElement.setAttribute('role', 'menu')
        
        if (this.element.tagName === 'INPUT') {
          this.element.parentNode.appendChild(this.resultsElement)
        } else {
          this.element.appendChild(this.resultsElement)
        }
      }
    } else {
      this.resultsElement = this.resultsTarget
    }

    if (!this.hasHiddenTarget) {
      const idSelector = this.idElementValue || this.inputElement.dataset.idElement
      if (idSelector) {
        this.hiddenElement = this.element.closest('form')?.querySelector(idSelector) || document.querySelector(idSelector)
        if (this.hiddenElement) {
          Logger.debug('Autocomplete: hidden element found', { selector: idSelector, element: this.hiddenElement })
        } else {
          Logger.warn('Autocomplete: hidden element NOT found', { selector: idSelector })
        }
      }
    } else {
      this.hiddenElement = this.hiddenTarget
      Logger.debug('Autocomplete: hidden element found via target', { element: this.hiddenElement })
    }

    this.resultsElement.hidden = true
    this.inputElement.setAttribute('autocomplete', 'off')
    this.inputElement.setAttribute('spellcheck', 'false')

    this.mouseDown = false

    // Bind methods
    this.onInputChange = this.debounce(this.onInputChange.bind(this), this.debounceMsValue)
    this.onResultsClick = this.onResultsClick.bind(this)
    this.onResultsMouseDown = this.onResultsMouseDown.bind(this)
    this.onInputBlur = this.onInputBlur.bind(this)
    this.onInputFocus = this.onInputFocus.bind(this)
    this.onKeydown = this.onKeydown.bind(this)

    // Add event listeners
    this.inputElement.addEventListener('keydown', this.onKeydown)
    this.inputElement.addEventListener('focus', this.onInputFocus)
    this.inputElement.addEventListener('blur', this.onInputBlur)
    this.inputElement.addEventListener('input', this.onInputChange)
    this.resultsElement.addEventListener('mousedown', this.onResultsMouseDown)
    this.resultsElement.addEventListener('click', this.onResultsClick)
  }

  removeEventListeners() {
    this.inputElement.removeEventListener('keydown', this.onKeydown)
    this.inputElement.removeEventListener('focus', this.onInputFocus)
    this.inputElement.removeEventListener('blur', this.onInputBlur)
    this.inputElement.removeEventListener('input', this.onInputChange)
    this.resultsElement.removeEventListener('mousedown', this.onResultsMouseDown)
    this.resultsElement.removeEventListener('click', this.onResultsClick)
  }

  initCommandK() {
    if (!this.commandKEnabledValue) return

    this.onCommandK = (e) => {
      if (e.metaKey && e.code === 'KeyK') {
        e.preventDefault()
        this.searchInputElement.focus()
      }
    }
    document.addEventListener('keydown', this.onCommandK)
  }

  get searchInputElement() {
    return document.getElementById('search-input') || this.inputElement
  }

  sibling(next) {
    const options = Array.from(this.resultsElement.querySelectorAll('[role="option"]'))
    const selected = this.resultsElement.querySelector('[aria-selected="true"]')
    const index = options.indexOf(selected)
    const sibling = next ? options[index + 1] : options[index - 1]
    const def = next ? options[0] : options[options.length - 1]
    return sibling || def
  }

  select(target) {
    for (const el of this.resultsElement.querySelectorAll('[aria-selected="true"]')) {
      el.removeAttribute('aria-selected')
      el.classList.remove('active')
    }
    target.setAttribute('aria-selected', 'true')
    target.classList.add('active')
    this.inputElement.setAttribute('aria-activedescendant', target.id)
  }

  onKeydown(event) {
    switch (event.key) {
      case 'Escape':
        if (!this.resultsElement.hidden) {
          this.hideAndRemoveOptions()
          event.stopPropagation()
          event.preventDefault()
        }
        break
      case 'ArrowDown':
        {
          const item = this.sibling(true)
          if (item) this.select(item)
          event.preventDefault()
        }
        break
      case 'ArrowUp':
        {
          const item = this.sibling(false)
          if (item) this.select(item)
          event.preventDefault()
        }
        break
      case 'Tab':
        {
          const selected = this.resultsElement.querySelector('[aria-selected="true"]')
          if (selected) {
            this.commit(selected)
          }
        }
        break
      case 'Enter':
        {
          const selected = this.resultsElement.querySelector('[aria-selected="true"]')
          if (selected && !this.resultsElement.hidden) {
            this.commit(selected)
            event.preventDefault()
          }
        }
        break
    }
  }

  onInputFocus() {
    if (this.searchModeValue) {
      this.fetchResults()
    }
  }

  onInputBlur() {
    this.resultsElement.hidden = true

    // Check if the current input value matches our committed label.
    // If not, the user typed something else without selecting an option.
    // We should clear the hidden element in this case.
    const query = this.inputElement.value.trim()
    if (this.hiddenElement) {
      if (query !== this.committedLabel) {
        const oldValue = this.hiddenElement.value
        this.hiddenElement.value = ''
        this.committedLabel = ''
        if (oldValue !== '') {
          this.hiddenElement.dispatchEvent(new Event('change', { bubbles: true }))
        }
      }
    }
  }

  commit(selected) {
    if (selected.getAttribute('aria-disabled') === 'true') return

    if (selected instanceof HTMLAnchorElement) {
      selected.click()
      this.resultsElement.hidden = true
      return
    }

    const textValue = selected.textContent.trim()
    const label = selected.getAttribute('data-autocomplete-label') || textValue
    const value = selected.getAttribute('data-autocomplete-value') || textValue
    
    Logger.debug('Autocomplete: committing selection', { label, value, textValue })
    
    this.committedLabel = label
    this.inputElement.value = label

    if (this.hiddenElement) {
      this.hiddenElement.value = value
      Logger.debug('Autocomplete: updated hidden element', { elementId: this.hiddenElement.id, value: this.hiddenElement.value })
      // Trigger change on hidden element too
      this.hiddenElement.dispatchEvent(new Event('change', { bubbles: true }))
    } else {
      this.inputElement.value = value
    }

    // Dispatch change event on the input field as well
    this.inputElement.dispatchEvent(new Event('change', { bubbles: true }))

    // Dispatch custom autocomplete.change event
    this.element.dispatchEvent(new CustomEvent('autocomplete.change', {
      bubbles: true,
      detail: { value: value, textValue: textValue, label: label }
    }))

    this.inputElement.focus()
    this.hideAndRemoveOptions()

    // Handle search mode submission
    if (this.searchModeValue) {
      this.submit()
    }

    // Handle updating related elements
    this.updateElements(selected)
  }

  updateElements(selected) {
    const updates = this.hasUpdateElementsValue ? this.updateElementsValue : null
    if (!updates) return

    Logger.debug('Autocomplete: updating related elements', { updates })

    Object.keys(updates).forEach(key => {
      const selector = updates[key]
      const dataKey = key.replace(/_/g, '-')
      const value = selected.getAttribute(`data-${dataKey}`)

      Logger.debug(`Autocomplete: updating ${key}`, { selector, dataKey, value })

      if (selector && value) {
        const element = this.element.closest('form')?.querySelector(selector) || document.querySelector(selector)
        if (element) {
          element.value = value
          Logger.debug(`Autocomplete: updated ${selector} to ${value}`)
          // Trigger change event for updated element
          element.dispatchEvent(new Event('change', { bubbles: true }))
        } else {
          Logger.warn(`Autocomplete: element not found for selector ${selector}`)
        }
      }
    })
  }

  submit() {
    const globalId = this.hiddenElement?.value
    if (globalId && globalId.length > 0) {
      this.reset()
      // Use Turbo if available, otherwise use window.location
      if (typeof Turbo !== 'undefined') {
        Turbo.visit('/search/?sgid=' + globalId)
      } else {
        window.location.href = '/search/?sgid=' + globalId
      }
    }
  }

  reset() {
    this.inputElement.value = ''
    if (this.hiddenElement) {
      this.hiddenElement.value = ''
    }
  }

  onResultsClick(event) {
    if (!(event.target instanceof Element)) return
    const selected = event.target.closest('[role="option"]')
    if (selected) this.commit(selected)
  }

  onResultsMouseDown(event) {
    event.preventDefault()
  }

  onInputChange() {
    const query = this.inputElement.value.trim()
    if (!query && this.hiddenElement && this.hiddenElement.value !== '') {
      this.hiddenElement.value = ''
      this.committedLabel = ''
      this.hiddenElement.dispatchEvent(new Event('change', { bubbles: true }))
    }
    this.element.removeAttribute('value')
    this.fetchResults()
  }

  identifyOptions() {
    let id = 0
    for (const el of this.resultsElement.querySelectorAll('[role="option"]:not([id])')) {
      el.id = `${this.resultsElement.id}-option-${id++}`
    }
  }

  hideAndRemoveOptions() {
    this.resultsElement.hidden = true
    this.resultsElement.innerHTML = null
  }

  fetchResults() {
    const query = this.inputElement.value.trim()
    if (!query || query.length < this.minLengthValue) {
      this.hideAndRemoveOptions()
      return
    }

    if (!this.urlValue) return

    const url = new URL(this.urlValue, window.location.href)
    const params = new URLSearchParams(url.search.slice(1))
    params.append('q', query)
    url.search = params.toString()

    this.element.dispatchEvent(new CustomEvent('loadstart'))

    fetch(url.toString(), {
      headers: {
        'Accept': 'text/html',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then(response => response.text())
      .then(html => {
        // Direct DOM manipulation instead of turbo streams
        this.resultsElement.innerHTML = html
        this.identifyOptions()
        const hasResults = !!this.resultsElement.querySelector('[role="option"]')
        this.resultsElement.classList.add('show')
        this.resultsElement.hidden = !hasResults
        this.element.dispatchEvent(new CustomEvent('load'))
        this.element.dispatchEvent(new CustomEvent('loadend'))
      })
      .catch(() => {
        this.element.dispatchEvent(new CustomEvent('error'))
        this.element.dispatchEvent(new CustomEvent('loadend'))
      })
  }

  open() {
    if (!this.resultsElement.hidden) return
    this.resultsElement.hidden = false
    this.element.setAttribute('aria-expanded', 'true')
    this.element.dispatchEvent(new CustomEvent('toggle', {
      detail: { input: this.input, results: this.results }
    }))
  }

  close() {
    if (this.resultsElement.hidden) return
    this.resultsElement.hidden = true
    this.inputElement.removeAttribute('aria-activedescendant')
    this.element.setAttribute('aria-expanded', 'false')
    this.element.dispatchEvent(new CustomEvent('toggle', {
      detail: { input: this.input, results: this.results }
    }))
  }

  // Public API methods
  setValue(value, textValue = null) {
    this.inputElement.value = textValue || value
    this.committedLabel = this.inputElement.value.trim()
    if (this.hiddenElement) {
      this.hiddenElement.value = value
    }
  }

  getValue() {
    return this.hiddenElement ? this.hiddenElement.value : this.inputElement.value
  }

  clear() {
    this.inputElement.value = ''
    this.committedLabel = ''
    if (this.hiddenElement) {
      this.hiddenElement.value = ''
    }
    this.hideAndRemoveOptions()
  }

  focus() {
    this.inputElement.focus()
  }

  // Handle city selection from autocomplete (for compatibility)
  handleCitySelection() {
    Logger.debug('City selected, ready for search')
    // This method can be overridden in subclasses for specific behavior
  }
}
